import { useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { BarChart3, Footprints, Home, LogOut, Medal, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useSession } from "@/lib/store/session";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { primaryNav, secondaryNav } from "./nav";

const tabs = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/runs", label: "Activities", icon: Footprints },
  { to: "/pb", label: "PBs", icon: Medal },
  { to: "/stats", label: "Stats", icon: BarChart3 },
];

const tabClass = (active: boolean) =>
  cn(
    "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-md py-1.5 text-[11px] font-medium text-fg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 [&_svg]:size-5",
    active && "text-accent",
  );

export function BottomTabBar() {
  const loggedIn = useSession((s) => !!s.userId);
  const logout = useSession((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!loggedIn) return null;

  const tabPaths = new Set(tabs.map((t) => t.to));
  const more = [...primaryNav.filter((i) => !tabPaths.has(i.to)), ...secondaryNav];

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 backdrop-blur tab:hidden"
      style={{ paddingBottom: "var(--spacing-safe-bottom)" }}
      aria-label="Primary"
    >
      <div className="flex h-14 items-stretch px-1">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} className={({ isActive }) => tabClass(isActive)}>
            <t.icon aria-hidden />
            {t.label}
          </NavLink>
        ))}
        <Sheet open={open} onOpenChange={setOpen}>
          <button type="button" className={tabClass(open)} onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>
            <MoreHorizontal aria-hidden />
            More
          </button>
          <SheetContent side="bottom" className="p-4" hideClose>
            <SheetTitle className="text-base">More</SheetTitle>
            <div className="grid grid-cols-3 gap-2">
              {more.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex flex-col items-center gap-1.5 rounded-md border border-border bg-surface-2/60 px-2 py-3 text-center text-xs font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
                      isActive && "border-accent/40 bg-accent/10 text-accent",
                    )
                  }
                >
                  <i.icon className="size-5" aria-hidden />
                  {i.short ?? i.label}
                </NavLink>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full text-danger"
              onClick={() => {
                setOpen(false);
                logout();
                navigate("/auth");
              }}
            >
              <LogOut /> Sign out
            </Button>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
